
import React from "react";
import { UseFormRegister, FieldErrors } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CourseFormData } from "../types";

// US states array for the dropdown
const US_STATES = [
  { value: "", label: "Select a state" },
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
  { value: "DC", label: "District of Columbia" },
];

interface BasicInfoFieldsProps {
  register: UseFormRegister<CourseFormData>;
  errors: FieldErrors<CourseFormData>;
}

export function BasicInfoFields({ register, errors }: BasicInfoFieldsProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="space-y-2">
        <Label htmlFor="course_name">Course Name</Label>
        <Input
          id="course_name"
          {...register("course_name", { required: "Course name is required" })}
        />
        {errors.course_name && <p className="text-sm text-red-500">{errors.course_name.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="course_type">Course Type</Label>
        <Input
          id="course_type"
          {...register("course_type", { required: "Course type is required" })}
        />
        {errors.course_type && <p className="text-sm text-red-500">{errors.course_type.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="language">Language</Label>
        <Input
          id="language"
          {...register("language", { required: "Language is required" })}
          defaultValue="English"
        />
        {errors.language && <p className="text-sm text-red-500">{errors.language.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="state">State</Label>
        <select
          id="state"
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          {...register("state")}
        >
          {US_STATES.map((state) => (
            <option key={state.value} value={state.value}>
              {state.label}
            </option>
          ))}
        </select>
        {errors.state && <p className="text-sm text-red-500">{errors.state.message}</p>}
      </div>

      <div className="space-y-2 md:col-span-2">
        <Label htmlFor="duration_hours">Duration (hours)</Label>
        <Input
          id="duration_hours"
          type="number"
          {...register("duration_hours", { 
            required: "Duration is required",
            min: { value: 0, message: "Duration must be positive" }
          })}
        />
        {errors.duration_hours && <p className="text-sm text-red-500">{errors.duration_hours.message}</p>}
      </div>
    </div>
  );
}
